/*
 * cs-form.js — shared client for Bumpology self-service forms.
 *
 * Handles the contact, EU-withdrawal and order-status forms. Each form serializes
 * its named fields to JSON, POSTs to the staff backend, and toggles success/error
 * UI in place — the visitor never leaves bumpology.com.
 *
 * Bot filtering (no Cloudflare Turnstile):
 *   - Honeypot field `hp` (must stay empty) on every form.
 *   - Contact form: Google reCAPTCHA v3 (invisible). A fresh token is generated on
 *     submit and sent as `recaptcha_token`.
 *   - EU-withdrawal + order-status: a signed form token fetched from the backend on
 *     page load and sent back as `form_token`.
 *
 * Markup contract (set by each section):
 *   <form
 *     data-cs-form
 *     data-cs-endpoint="https://staff.bumpology.com/api/contact/"
 *     [data-cs-keep]                          keep the form visible after success
 *     [data-cs-review]                        two-step fill -> review -> confirm flow
 *     [data-cs-recaptcha data-cs-recaptcha-key="..." data-cs-recaptcha-action="contact"]
 *     [data-cs-token-url="https://staff.bumpology.com/api/form-token/"]>
 *
 *     ...named inputs (name="email" etc.)...
 *     <input class="cs-hp" name="hp" tabindex="-1" autocomplete="off" aria-hidden="true">
 *     <div data-cs-success hidden></div>
 *     <div data-cs-error hidden></div>
 *   </form>
 *
 * The form dispatches `cs:success` (detail = response JSON) and `cs:error`
 * (detail = { message }) so sections can render custom output (e.g. tracking).
 */
(function () {
  'use strict';

  var RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=';
  var RECAPTCHA_FAIL = "Couldn't verify you're human — please refresh and try again.";
  var TOKEN_FAIL = "Couldn't verify your request — please refresh and try again.";
  var NETWORK_FAIL = 'We could not reach the server. Please try again or email us directly.';
  var GENERIC_FAIL = 'Sorry, something went wrong. Please try again or email us directly.';

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  // ---- Google reCAPTCHA v3 (invisible) -------------------------------------
  var recaptchaLoaded = false;
  function loadRecaptcha(key) {
    if (recaptchaLoaded || !key) return;
    recaptchaLoaded = true;
    var s = document.createElement('script');
    s.src = RECAPTCHA_SRC + encodeURIComponent(key);
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }
  // Resolve to a fresh action token, or reject if reCAPTCHA isn't usable.
  function getRecaptchaToken(key, action) {
    return new Promise(function (resolve, reject) {
      if (!window.grecaptcha || !window.grecaptcha.execute) { reject(new Error('recaptcha')); return; }
      try {
        window.grecaptcha.ready(function () {
          window.grecaptcha.execute(key, { action: action }).then(resolve, function () { reject(new Error('recaptcha')); });
        });
      } catch (e) { reject(new Error('recaptcha')); }
    });
  }

  // ---- Signed form token (eu-withdrawal, order-status) ---------------------
  // GET on page load so the token is at least ~2s old by submit time.
  function fetchFormToken(url) {
    return fetch(url, { method: 'GET' })
      .then(function (r) { return r.json(); })
      .then(function (b) { if (b && b.ok && b.token) return b.token; throw new Error('token'); });
  }

  function serialize(form) {
    var data = {};
    new FormData(form).forEach(function (value, key) { data[key] = value; });
    return data;
  }

  // Two-step: validate step 1, copy values into the review panel, reveal it.
  function wireReview(form) {
    var stepForm = form.querySelector('[data-cs-step="form"]');
    var stepReview = form.querySelector('[data-cs-step="review"]');
    var cont = form.querySelector('[data-cs-continue]');
    var back = form.querySelector('[data-cs-back]');
    if (!stepForm || !stepReview || !cont) return;

    cont.addEventListener('click', function () {
      var invalid = false;
      stepForm.querySelectorAll('input, textarea, select').forEach(function (el) {
        if (el.classList.contains('cs-hp')) return;
        if (!el.checkValidity()) { el.reportValidity(); invalid = true; }
      });
      if (invalid) return;

      form.querySelectorAll('[data-cs-review-for]').forEach(function (slot) {
        var field = form.elements[slot.getAttribute('data-cs-review-for')];
        var val = field ? field.value.trim() : '';
        slot.textContent = val || '—';
      });
      hide(stepForm);
      show(stepReview);
      stepReview.setAttribute('tabindex', '-1');
      stepReview.focus();
    });

    if (back) {
      back.addEventListener('click', function () {
        hide(stepReview);
        show(stepForm);
      });
    }
  }

  function handle(form) {
    var endpoint = form.getAttribute('data-cs-endpoint');
    var successEl = form.querySelector('[data-cs-success]');
    var errorEl = form.querySelector('[data-cs-error]');
    var keep = form.hasAttribute('data-cs-keep');

    var useRecaptcha = form.hasAttribute('data-cs-recaptcha');
    var recaptchaKey = form.getAttribute('data-cs-recaptcha-key');
    var recaptchaAction = form.getAttribute('data-cs-recaptcha-action') || 'submit';

    var tokenUrl = form.getAttribute('data-cs-token-url');
    var formTokenPromise = null; // primed on page load below

    if (useRecaptcha) loadRecaptcha(recaptchaKey);
    if (tokenUrl) formTokenPromise = fetchFormToken(tokenUrl).catch(function () { return null; });
    if (form.hasAttribute('data-cs-review')) wireReview(form);

    function showError(message) {
      form.dispatchEvent(new CustomEvent('cs:error', { detail: { message: message } }));
      if (errorEl) { errorEl.textContent = message; show(errorEl); errorEl.setAttribute('tabindex', '-1'); errorEl.focus(); }
    }

    function onSuccess(body) {
      form.dispatchEvent(new CustomEvent('cs:success', { detail: body }));
      if (!keep) {
        hide(form.querySelector('[data-cs-step="review"]'));
        hide(form.querySelector('[data-cs-step="form"]'));
        form.querySelectorAll('.cs-form__body').forEach(hide);
      }
      show(successEl);
      if (successEl) { successEl.setAttribute('tabindex', '-1'); successEl.focus(); }
    }

    function post(payload) {
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          return { status: res.status, ok: res.ok, body: body };
        });
      });
    }

    // Ensure a usable form token, refetching once if the primed one failed.
    function ensureFormToken() {
      if (!formTokenPromise) formTokenPromise = fetchFormToken(tokenUrl);
      return formTokenPromise.then(
        function (t) { if (t) return t; formTokenPromise = fetchFormToken(tokenUrl); return formTokenPromise; }
      );
    }

    // Attach the right verification field(s) to the payload.
    function acquireVerification(payload) {
      var chain = Promise.resolve(payload);
      if (useRecaptcha) {
        chain = chain.then(function (p) {
          return getRecaptchaToken(recaptchaKey, recaptchaAction).then(function (t) { p.recaptcha_token = t; return p; });
        });
      }
      if (tokenUrl) {
        chain = chain.then(function (p) {
          return ensureFormToken().then(
            function (t) { if (!t) throw new Error('token'); p.form_token = t; return p; },
            function () { throw new Error('token'); }
          );
        });
      }
      return chain;
    }

    // POST, and on a 403 for token forms refetch a fresh token and retry once.
    // The retry waits ~2.2s so the new token satisfies the backend's "≥2s old" rule.
    function submitWithRetry(payload) {
      return post(payload).then(function (result) {
        if (!tokenUrl || result.status !== 403) return result;
        return fetchFormToken(tokenUrl).then(function (t) {
          formTokenPromise = Promise.resolve(t);
          payload.form_token = t;
          return new Promise(function (resolve) { setTimeout(resolve, 2200); }).then(function () { return post(payload); });
        }, function () { return result; });
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      hide(errorEl);

      var submitBtn = form.querySelector('[type="submit"]');
      var originalLabel = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = submitBtn.getAttribute('data-cs-loading') || 'Sending…';
      }

      acquireVerification(serialize(form))
        .then(function (payload) { return submitWithRetry(payload); })
        .then(function (result) {
          var body = result.body || {};
          if (result.ok && body.ok !== false) onSuccess(body);
          else showError(body.error || GENERIC_FAIL);
        })
        .catch(function (err) {
          if (err && err.message === 'recaptcha') showError(RECAPTCHA_FAIL);
          else if (err && err.message === 'token') showError(TOKEN_FAIL);
          else showError(NETWORK_FAIL);
        })
        .then(function () {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalLabel; }
        });
    });
  }

  function init() {
    document.querySelectorAll('[data-cs-form]').forEach(handle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
